import React, { useState, useEffect } from 'react';
import { Users, Building2, Gamepad2, Map, Trophy, Medal } from 'lucide-react';

export default function Championships() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, regional, state

  useEffect(() => {
    fetchLeaderboard();
  }, [filter]);

  const fetchLeaderboard = async () => {
    try {
      // TODO: Replace with actual API call
      // const response = await championshipsAPI.getLeaderboard({ filter });
      // setLeaderboard(response.data);
      
      // Placeholder - empty for now since no games played yet
      setTimeout(() => {
        setLeaderboard([]);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading championships...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Championships</h1>
        <div className="flex gap-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
          >
            <option value="all">All Regions</option>
            <option value="midwest">Midwest</option>
            <option value="northeast">Northeast</option>
            <option value="south">South</option>
            <option value="west">West</option>
          </select>
          <button className="btn-primary">
            Export Data
          </button>
        </div>
      </div>

      {/* Championship Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Total Teams</h3>
            <Users size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-slate-600">0</p>
          <p className="text-sm text-gray-500 mt-2">Championship eligible</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Venues</h3>
            <Building2 size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-azure-600">3</p>
          <p className="text-sm text-gray-500 mt-2">Active locations</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Official Games</h3>
            <Gamepad2 size={32} className="text-orange-600" />
          </div>
          <p className="text-4xl font-bold text-orange-600">0</p>
          <p className="text-sm text-gray-500 mt-2">Counted for rankings</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">States</h3>
            <Map size={32} className="text-green-600" />
          </div>
          <p className="text-4xl font-bold text-green-600">2</p>
          <p className="text-sm text-gray-500 mt-2">IL, WI</p>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">Championship Leaderboard</h2>
          <p className="text-sm text-gray-600 mt-1">Top teams ranked by official game performance</p>
        </div>

        {leaderboard.length === 0 ? (
          <div className="text-center py-12">
            <Trophy size={64} className="text-yellow-500" />
            <p className="text-gray-500 text-lg mb-2">No championship data yet</p>
            <p className="text-gray-400">Teams will appear here after playing official games</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Rank</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Team</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Venue</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Location</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Total Points</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Games Played</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Wins</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Avg Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {leaderboard.map((team, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {idx === 0 && <Medal size={24} className="text-yellow-500 mr-2" />}
                      {idx === 1 && <Medal size={24} className="text-gray-400 mr-2" />}
                      {idx === 2 && <Medal size={24} className="text-amber-600 mr-2" />}
                      <span className="font-semibold text-gray-900">{idx + 1}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-semibold text-gray-900">{team.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{team.venue}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{team.location}</td>
                  <td className="px-6 py-4 font-semibold text-slate-600">{team.points}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{team.games}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{team.wins}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{team.avg.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
